import React from "react";
import { useResourceInfo } from "../../../../../../hooks/useResourceInfo";
import CustomTooltip from "../../../../../../components/common/CustomTooltip";
import { getDefaultUserName } from "../../../../../../constants/defaultUsers";

interface ResourcesProps {
    resources: string[];
    projectId: string;
}

interface CustomAvatarProps {
    name?: string;
    imageUrl?: string | null;
    className?: string;
    resourceId?: string; // Resource ID to show when name is not available
}

const getInitials = (fullName?: string) => {
    if (!fullName) return "??";

    const parts = fullName.trim().split(" ").filter(Boolean);

    if (parts.length > 1) {
        // Take first letter of first and last word
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    // If only one word, take first 2 letters
    return parts[0].slice(0, 2).toUpperCase();
};

const CustomAvatar = ({ name, imageUrl, className = "", resourceId }: CustomAvatarProps) => {
    const [imageError, setImageError] = React.useState(false);

    // Use name if available, otherwise use resourceId
    const displayName = name || resourceId;
    const initials = getInitials(displayName);

    const avatarContent = (
        <div className={`relative w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform duration-200 cursor-pointer ${className}`}>
            {imageUrl && !imageError ? (
                <img
                    src={imageUrl}
                    alt={displayName}
                    className="w-full h-full rounded-full object-cover"
                    onError={() => {
                        // Fallback to initials if image fails to load
                        setImageError(true);
                    }}
                />
            ) : (
                <div className="w-full h-full rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-white text-sm font-medium">
                    {initials}
                </div>
            )}
        </div>
    );

    // Show tooltip if we have either name or resourceId
    if (!displayName) return avatarContent;

    return (
        <CustomTooltip title={displayName} placement="top">
            {avatarContent}
        </CustomTooltip>
    );
};

const AvatarCounter = ({ count, remainingNames, className = "" }: { count: number; remainingNames?: string[]; className?: string }) => {
    const tooltipText = remainingNames && remainingNames.length > 0
        ? `Additional members: ${remainingNames.join(', ')}`
        : `${count} more member${count > 1 ? 's' : ''}`;

    const counterContent = (
        <div className={`relative w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-orange-500 dark:bg-orange-600 flex items-center justify-center text-white text-sm font-medium hover:scale-110 transition-transform duration-200 cursor-pointer ${className}`}>
            +{count}
        </div>
    );

    return (
        <CustomTooltip title={tooltipText} placement="top">
            {counterContent}
        </CustomTooltip>
    );
};

const Resources = ({ resources = [], projectId = '' }: ResourcesProps) => {
    const { resourcesLoading, getResourceInfo } = useResourceInfo();

    if (resources.length === 0) return null;

    const projectResources = resources.map(resourceId => {
        const creatorName = getDefaultUserName(resourceId);

        if (resourceId && !creatorName)
            return { ...getResourceInfo(resourceId), resourceId };

        return {
            name: creatorName,
            image_url: "https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp",
            resourceId
        };
    });

    const firstResource = projectResources[0];
    const secondResource = projectResources[1];

    if (!resourcesLoading && (!projectResources || !projectResources.length)) {
        return <></>;
    }

    return (
        <div className="px-2 whitespace-nowrap font-medium  w-full items-center">
            {projectResources.length > 0 && (
                <div className="flex items-center -space-x-2 h-full">
                    <CustomAvatar
                        name={firstResource?.name}
                        imageUrl={firstResource?.image_url}
                        resourceId={firstResource?.resourceId}
                        className="relative"
                    />
                    {secondResource && (
                        <CustomAvatar
                            name={secondResource?.name}
                            imageUrl={secondResource?.image_url}
                            resourceId={secondResource?.resourceId}
                            className="relative -ml-2"
                        />
                    )}
                    {projectResources.length > 2 && (
                        <AvatarCounter
                            count={projectResources.length - 2}
                            remainingNames={projectResources.slice(2).map(r => r?.name).filter(Boolean) as string[]}
                            className="relative -ml-2"
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default Resources;